import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  familyHasPaidPlan,
  getFamilyPlanPillLabel,
  getOverviewPlanStatusLine,
  normalizeFamilyPlan
} from '@/utils/familyPlan';
import './overview-plan-card.css';

export interface OverviewPlanCardProps {
  plan: string;
  isOwner: boolean;
  /** Owner/admin — bulk media ZIP (billing §5.7 carve-out; available on free) */
  canExportMedia?: boolean;
  onExportMediaClick?: () => void;
  /** Owner/admin — GEDCOM full/delta export (free on all tiers) */
  canExportGedcom?: boolean;
  onExportGedcomClick?: () => void;
  /** Non-owners only — share in subscription cost without managing billing */
  onContributeClick?: () => void;
  onUpgradeClick?: () => void;
  onManageBillingClick?: () => void;
  /** Owners — WeTree ledger of contributions, extensions, and renewals */
  onBillingHistoryClick?: () => void;
  /**
   * Optional renewal label for paid status line (e.g. "Apr 12, 2026").
   * When omitted, paid cards use a plain-language Family plan blurb instead.
   */
  nextChargeLabel?: string | null;
  /** Optional billing interval for paid status ("month" | "year") */
  billingInterval?: 'month' | 'year' | null;
}

/**
 * Full-width plan & billing card for Family Overview (beneath the stats tiles).
 * Owners upgrade / manage billing / view history; non-owners can contribute.
 */
const OverviewPlanCard: React.FC<OverviewPlanCardProps> = ({
  plan,
  isOwner,
  canExportMedia = false,
  onExportMediaClick,
  canExportGedcom = false,
  onExportGedcomClick,
  onContributeClick,
  onUpgradeClick,
  onManageBillingClick,
  onBillingHistoryClick,
  nextChargeLabel = null,
  billingInterval = null
}) => {
  const { t } = useTranslation('overview');
  const normalized = normalizeFamilyPlan(plan);
  const isPaidTier = familyHasPaidPlan(normalized);
  const label = getFamilyPlanPillLabel(normalized);
  const statusLine = getOverviewPlanStatusLine({
    plan: normalized,
    isOwner,
    nextChargeLabel,
    billingInterval
  });

  return (
    <div className="overview-plan-card" data-tour="overview-plan" aria-label={t('planCard.ariaLabel')}>
      <div className="overview-plan-card__left">
        <span className={`overview-plan-card__pill overview-plan-card__pill--${normalized}`}>{label}</span>
        <span className="overview-plan-card__status">{statusLine}</span>
      </div>
      <div className="overview-plan-card__actions">
        {canExportGedcom && onExportGedcomClick ? (
          <button
            type="button"
            className="overview-plan-card__action"
            data-tour="overview-export-gedcom"
            title={t('planCard.exportGedcomTitle')}
            onClick={onExportGedcomClick}
          >
            {t('planCard.exportGedcom')}
          </button>
        ) : null}
        {canExportMedia && onExportMediaClick ? (
          <button
            type="button"
            className="overview-plan-card__action"
            title={t('planCard.exportMediaTitle')}
            onClick={onExportMediaClick}
          >
            {t('planCard.exportMedia')}
          </button>
        ) : null}
        {!isOwner && onContributeClick ? (
          <button
            type="button"
            className="overview-plan-card__action"
            title={t('planCard.contributeTitle')}
            onClick={onContributeClick}
          >
            {t('planCard.contribute')}
          </button>
        ) : null}
        {isOwner && !isPaidTier ? (
          <button
            type="button"
            className="overview-plan-card__action"
            title={t('planCard.upgradeTitle')}
            onClick={onUpgradeClick}
            disabled={!onUpgradeClick}
          >
            {t('planCard.upgrade')}
          </button>
        ) : null}
        {isOwner && normalized === 'paid' ? (
          <button
            type="button"
            className="overview-plan-card__action"
            title={t('planCard.manageBillingTitle')}
            onClick={onManageBillingClick}
            disabled={!onManageBillingClick}
          >
            {t('planCard.manageBilling')}
          </button>
        ) : null}
        {isOwner && onBillingHistoryClick ? (
          <button
            type="button"
            className="overview-plan-card__action"
            title={t('planCard.billingHistoryTitle')}
            onClick={onBillingHistoryClick}
          >
            {t('planCard.billingHistory')}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default OverviewPlanCard;
